import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import * as Sentry from '@sentry/react';

import formGenerator from '../const/forms';
import formValidationGenerator, { formValidationToCustomFields } from '../const/validations';

/**
 * Ventana de dedupe (ms) por nombre de campo, siguiendo el patrón de
 * `observability/pusherInstrumentation.js`. Un handler enganchado mal dispara
 * en cada tecla; sin esto un solo formulario mal escrito quemaría cuota de
 * Sentry en un minuto.
 */
const DEDUPE_WINDOW_MS = 30 * 1000;

/**
 * Traduce lo que React le pasa a un handler al argumento que espera
 * `triggerValidation` de react-hook-form 4.
 *
 * `UsersAction` engancha la validación como `onChange={triggerValidation}`.
 * React invoca el handler con el SyntheticEvent, pero react-hook-form espera el
 * NOMBRE del campo y hace `payload.split(...)` sobre él: sobre un evento eso
 * revienta con "TypeError: <algo>.split is not a function" — y como
 * `triggerValidation` es async y nadie espera su promesa, sale como unhandled
 * rejection y la validación en vivo simplemente nunca corre. Ver DASHBOARD-ZC-GS.
 *
 * ALCANCE REAL, revisados los 22 puntos de llamada uno por uno: 20 de 22 pasan
 * por `Input`/`Select`/`TextArea`, que ya invocan `onChange(name)` con el nombre
 * del campo, así que nunca estuvieron rotos. Rotos de verdad: `UsersAction`
 * L405 y L416 — **1 pantalla, 2 campos**. No hay media docena de pantallas
 * rotas esperando; que nadie salga a buscarlas.
 *
 * Se normaliza aquí, en el hook, y no en esos dos `onChange`, porque es un solo
 * lugar en vez de dos y cubre de una vez a todos los consumidores de
 * `useFormBuilder`.
 *
 * HASTA DÓNDE LLEGA ESTA NORMALIZACIÓN — se declara porque es más chica de lo
 * que suena, y ninguno de los dos huecos avisa cuando se cae en él:
 *
 *  1. **Sólo cubre a quien pasa por `useFormBuilder`.** Hoy 16 archivos llaman
 *     `useForm` de react-hook-form directamente (`AddAgentView`, `EditAgentView`,
 *     `AddAdminView`, `EditAdminView`, `AddReport`, `EditReportView`,
 *     `RegisterView`, `ResetPassword`, `SendResetPasswordEmail`, `EditClient`,
 *     `roleActionPage`, `OnboardingStep`, `OnboardingEmail`, `OnboardingLayout`,
 *     `EditClientInput`, `template-form`) y ahí el `triggerValidation` que
 *     circula es el crudo de RHF, sin esta traducción. Ninguno engancha hoy un
 *     handler crudo, pero esto NO es un contrato cerrado del que nadie pueda
 *     salirse: un `onChange={triggerValidation}` nuevo en cualquiera de esos 16
 *     vuelve a reventar igual que `UsersAction`.
 *
 *  2. **Un valor y un nombre de campo son los dos strings**, y entran por la
 *     misma rama de `toFieldName`. Si un componente llama `onChange(valor)`, RHF
 *     valida un campo inexistente: no lanza, no marca error, no valida nada.
 *     `Select.component.jsx` tiene exactamente una rama así (`followUp` /
 *     `followUpTemplate` → `onChange(e.target.value)`); hoy **ningún call site
 *     enciende esas props**, y hay un test que se pone rojo el día que alguien
 *     las encienda. Ver
 *     `components/shared/components/select/__tests__/Select.triggerValidation.test.jsx`.
 *
 *     Este hueco **ya no es mudo**: `avisarSiNoEsUnCampo` reporta a Sentry
 *     cuando se pide validar un identificador que el formulario no registró.
 *     Sigue sin validar nada — eso no se puede arreglar desde aquí, porque el
 *     nombre correcto no se puede adivinar — pero deja de pasar callado.
 *
 * @param {string|string[]|Event|undefined} payload
 * @returns {string|string[]|undefined} nombre(s) de campo, o `undefined` para
 *          validar el formulario completo (comportamiento por defecto de RHF).
 */
const toFieldName = (payload) => {
  if (typeof payload === 'string' || Array.isArray(payload)) return payload;
  const name = payload && payload.target && payload.target.name;
  return typeof name === 'string' && name ? name : undefined;
};

/**
 * Nombres que el formulario tiene REGISTRADOS en este momento.
 *
 * `control.fieldsRef` es parte del tipo público `Control` de react-hook-form
 * 4.10 (`dist/types.d.ts:149`) y es la única fuente honesta: incluye los campos
 * dinámicos (`useFieldArray`, los `registerForm({ name })` dentro de un efecto)
 * que la lista `inputs` del hook no conoce. Usar `inputs` daría falsos positivos
 * en `DatasheetConfigurationAction` y `QuickAnswersAction`.
 */
const camposRegistrados = (control) => {
  const fields = control && control.fieldsRef && control.fieldsRef.current;
  return fields ? Object.keys(fields) : [];
};

/**
 * Nombres que el formulario DECLARA, sacados de `inputs`. Según el call site
 * vienen como strings (`['shortcut', 'message']`) o como objetos de
 * configuración (`editClientInformation` pasa los campos del datasheet).
 *
 * Esto es lo que hace el aviso determinista: `inputs` se conoce en el mismo
 * render en el que se llama, mientras que el registro real ocurre en el commit.
 * Preguntar sólo por lo registrado obliga a esperar un turno del event loop, y
 * eso resultó ser una carrera: en pruebas repetidas el veredicto caía a veces
 * antes y a veces después del commit. Se compara contra lo declarado, y lo
 * registrado se suma encima para los campos dinámicos que `inputs` no conoce
 * (`useFieldArray`, los `register({ name })` dentro de un efecto).
 */
const nombresDeclarados = (inputs) => (Array.isArray(inputs) ? inputs : [])
  .map((entrada) => (typeof entrada === 'string'
    ? entrada
    : entrada && (entrada.name || entrada.field)))
  .filter((nombre) => typeof nombre === 'string' && nombre);

const useFormBuilder = (
  inputs, defaultValues = {}, byFieldType = false, checkToReset = false, dynamicFields = [], checkToResetDynamicFields = false,
) => {
  const [form, setForm] = useState(formGenerator(inputs, byFieldType, dynamicFields));

  const {
    register,
    unregister,
    errors,
    watch,
    triggerValidation,
    handleSubmit,
    setError,
    getValues,
    setValue,
    control,
    reset,
  } = useForm({
    validationSchema: byFieldType ? formValidationToCustomFields(inputs) : formValidationGenerator(inputs),
    defaultValues,
  });

  // Map<nombre, msDelÚltimoReporte>. Vive con la instancia del formulario: al
  // desmontar la pantalla se va con ella, así que una visita nueva vuelve a
  // reportar en vez de quedar suprimida para siempre.
  const ultimoAvisoPorCampo = useRef(new Map());

  /**
   * Objeción 1 de Arturo (PR #231): que un identificador que NO es un campo del
   * formulario deje de pasar callado.
   *
   * Por qué aquí y no en `toFieldName`: `toFieldName` es un traductor puro que
   * sólo mira la forma del payload — no sabe, ni debe saber, qué campos existen.
   * Qué campos hay sólo lo sabe el hook. Meterle el formulario al traductor lo
   * volvería imposible de probar solo y mezclaría dos responsabilidades.
   *
   * Qué NO se hace: no se corrige el nombre ni se cae a "validar todo". El
   * nombre correcto no se puede adivinar, y validar todo marcaría de rojo campos
   * que el usuario no ha tocado. El comportamiento no cambia — lo único que
   * cambia es que ahora se ve.
   *
   * PII: el texto recibido NO se manda. Todo el punto de este PR es que los
   * datos de cliente no acaben en Sentry, y el caso típico de este aviso es
   * justamente alguien pasando el VALOR de un campo (un teléfono, un correo) en
   * vez del nombre. Viajan los nombres de campo —identificadores de código, no
   * datos—, que bastan para ubicar el formulario, más una pista de causa que se
   * calcula aquí sin exponer el valor.
   */
  const avisarSiNoEsUnCampo = useCallback((nombre) => {
    if (typeof nombre !== 'string' || !nombre) return;

    const declarados = nombresDeclarados(inputs);
    const registrados = camposRegistrados(control);
    const conocidos = declarados.concat(registrados);
    // Formulario que todavía no declara ni registra nada: no hay nada que
    // afirmar, ni forma de distinguir un nombre malo de uno que aún no llega.
    if (!conocidos.length) return;
    if (conocidos.indexOf(nombre) !== -1) return;

    const ahora = Date.now();
    const previo = ultimoAvisoPorCampo.current.get(nombre);
    if (previo !== undefined && ahora - previo < DEDUPE_WINDOW_MS) return;
    ultimoAvisoPorCampo.current.set(nombre, ahora);

    // ¿Es el valor de algún campo del propio formulario? Esa es la firma de
    // `onChange(valor)` en vez de `onChange(name)`. Se compara aquí y se manda
    // sólo el veredicto, nunca el valor.
    let causa = 'nombre_desconocido';
    try {
      const valores = Object.values(getValues() || {});
      if (valores.some((v) => typeof v === 'string' && v === nombre)) {
        causa = 'valor_en_vez_de_nombre';
      }
    } catch (e) {
      // `getValues` no debería lanzar; si lo hace, el aviso sale sin pista.
      causa = 'indeterminada';
    }

    Sentry.captureMessage('useFormBuilder.campo_no_registrado', {
      level: 'warning',
      tags: { component: 'useFormBuilder', causa },
      extra: { camposConocidos: conocidos, longitudDelTexto: nombre.length },
    });
  }, [inputs, control, getValues]);

  const triggerValidationByName = useCallback(
    (payload) => {
      const nombre = toFieldName(payload);
      if (Array.isArray(nombre)) nombre.forEach(avisarSiNoEsUnCampo);
      else avisarSiNoEsUnCampo(nombre);
      return triggerValidation(nombre);
    },
    [triggerValidation, avisarSiNoEsUnCampo],
  );

  useEffect(() => {
    if (checkToReset) {
      setForm(formGenerator(inputs, byFieldType, dynamicFields.length ? dynamicFields : null));
    }
  }, [inputs.length]);

  useEffect(() => {
    if (checkToResetDynamicFields) {
      setForm(formGenerator(inputs, byFieldType, dynamicFields.length ? dynamicFields : null));
    }
  }, [dynamicFields.length]);

  return {
    form,
    register,
    unregister,
    errors,
    watch,
    triggerValidation: triggerValidationByName,
    handleSubmit,
    setError,
    control,
    getValues,
    setValue,
    reset,
  };
};

export default useFormBuilder;
